import React, { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { FaEye } from "react-icons/fa";

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function SingleFacilityCard({ seed = 1 }) {
  const [open, setOpen] = useState(false);

  // Original chart values
  const originalData = useMemo(
    () => ({
      labels: ["Medical College", "District Hospital", "SDH", "RH", "PHC", "SC"],
      datasets: [
        { label: "Rural", data: [3, 11, 40, 255, 2675, 10711], backgroundColor: "#5e72e4" },
        { label: "Urban", data: [6, 11, 16, 31, 69, 5], backgroundColor: "#11cdef" },
      ],
    }),
    []
  );

  // Apply seed scaling
  const chartData = useMemo(() => {
    return {
      labels: originalData.labels,
      datasets: originalData.datasets.map((ds) => ({
        ...ds,
        data: ds.data.map((v) => Math.round(v * seed)),
        borderRadius: 6,
        stack: "a",
      })),
    };
  }, [originalData, seed]);

  const totalFacilities = useMemo(() => {
    return chartData.datasets.reduce((sum, ds) => sum + ds.data.reduce((a, b) => a + b, 0), 0);
  }, [chartData]);

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: "top" }, tooltip: { mode: "index", intersect: false } },
    interaction: { mode: "nearest", axis: "x", intersect: false },
    scales: {
      x: { stacked: true, ticks: { font: { size: 12 } } },
      y: { stacked: true, ticks: { font: { size: 12 } } },
    },
  };

  return (
    <>
      {/* Hardcoded article card */}
      <article
        className="flex items-center justify-between gap-2 p-1.5 bg-white border border-slate-100 rounded-md shadow-sm hover:shadow-md cursor-pointer mt-2"
        onClick={() => setOpen(true)}
      >
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
          <FaEye className="w-4 h-4" />
        </div>

        <div className="flex-1 text-right ml-1">
          <div className="text-xs font-medium text-slate-500">Total Facilities</div>
          <div className="mt-0.25 text-sm font-bold text-gray-800">{totalFacilities.toLocaleString()}</div>
          <div className="mt-0.25 text-[10px] text-slate-400">Rural + Urban</div>
        </div>
      </article>

      {/* Offcanvas */}
      {open && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <aside className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-auto">
            <div className="p-4 border-b flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">Facility types</h3>
                <p className="text-xs text-slate-500">Breakdown by Rural / Urban</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="px-3 py-1 rounded bg-slate-100 text-sm"
              >
                Close
              </button>
            </div>

            <div className="p-4">
              {/* Chart */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <Bar data={chartData} options={chartOptions} height={300} />
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
